import React, { useState, useEffect } from 'react';
import { Upload, X, MapPin, FileImage, Trash2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { validateImage, convertToWebP } from '../../utils/imageUtils';
import { Tour } from '../../types/tours';

interface BatchImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

interface ImageFile {
  file: File;
  preview: string;
  title: string;
  description: string;
}

const BatchImportModal: React.FC<BatchImportModalProps> = ({ 
  isOpen, 
  onClose, 
  onImportComplete 
}) => {
  const [selectedTourId, setSelectedTourId] = useState<string>('');
  const [tours, setTours] = useState<Tour[]>([]);
  const [imageFiles, setImageFiles] = useState<ImageFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    if (isOpen) {
      fetchTours();
    }
  }, [isOpen]);

  const fetchTours = async () => {
    try {
      const { data, error } = await supabase
        .from('tours')
        .select('id, name, slug')
        .order('display_order', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;
      setTours(data || []);
    } catch (err) {
      console.error('Error fetching tours:', err);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setError(null);

    const validFiles: ImageFile[] = [];

    for (const file of files) {
      const validationError = validateImage(file);
      if (validationError) {
        setError(`${file.name}: ${validationError}`);
        continue;
      }

      const preview = URL.createObjectURL(file);
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
      
      validFiles.push({
        file,
        preview,
        title: nameWithoutExt,
        description: `Image from ${nameWithoutExt}`
      });
    }

    setImageFiles(prev => [...prev, ...validFiles]);
  };

  const removeImage = (index: number) => {
    setImageFiles(prev => {
      const newFiles = [...prev];
      URL.revokeObjectURL(newFiles[index].preview);
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const updateImageData = (index: number, field: 'title' | 'description', value: string) => {
    setImageFiles(prev => prev.map((img, i) => 
      i === index ? { ...img, [field]: value } : img
    ));
  };

  const uploadImage = async (imageFile: ImageFile): Promise<string | null> => {
    try {
      const webpFile = await convertToWebP(imageFile.file);
      const fileName = `${Math.random()}.webp`;
      const filePath = `gallery/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('gallery')
        .upload(filePath, webpFile);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('gallery')
        .getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      return null;
    }
  };

  const handleBatchImport = async () => {
    if (imageFiles.length === 0) {
      setError('Please select at least one image');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const totalImages = imageFiles.length;
      let successCount = 0;

      for (let i = 0; i < imageFiles.length; i++) {
        const imageFile = imageFiles[i];
        
        // Upload image
        const imageUrl = await uploadImage(imageFile);
        
        if (imageUrl) {
          // Save to database
          const { error } = await supabase
            .from('gallery_images')
            .insert([{
              title: imageFile.title,
              description: imageFile.description,
              image_url: imageUrl,
              tour_id: selectedTourId || null
            }]);

          if (!error) {
            successCount++;
          }
        }

        setUploadProgress(Math.round(((i + 1) / totalImages) * 100));
      }

      if (successCount === totalImages) {
        onImportComplete();
        handleClose();
      } else {
        setError(`Successfully imported ${successCount} out of ${totalImages} images`);
      }
    } catch (err) {
      console.error('Error during batch import:', err);
      setError('Failed to import images');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    // Clean up preview URLs
    imageFiles.forEach(img => URL.revokeObjectURL(img.preview));
    setImageFiles([]);
    setSelectedTourId('');
    setError(null);
    setUploadProgress(0);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-[#1F2B1F] rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b border-white/10">
          <h2 className="text-xl font-semibold">Batch Import Images</h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Tour Selection */}
          <div>
            <label htmlFor="tour" className="block text-sm font-medium text-white/70 mb-2">
              Associate with Tour (optional)
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <select
                id="tour"
                value={selectedTourId}
                onChange={(e) => setSelectedTourId(e.target.value)}
                className="w-full pl-12 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E85C0D] text-white appearance-none"
              >
                <option value="" className="bg-[#1F2B1F]">No tour association</option>
                {tours.map((tour) => (
                  <option key={tour.id} value={tour.id} className="bg-[#1F2B1F]">
                    {tour.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-2">
              Select Images
            </label>
            <label className="relative cursor-pointer block">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                disabled={uploading}
                className="hidden"
              />
              <div className="border-2 border-dashed border-white/20 rounded-lg p-8 text-center hover:border-white/40 transition-colors">
                <FileImage className="w-12 h-12 mx-auto mb-4 text-white/40" />
                <span className="text-white/70">
                  {uploading ? 'Uploading...' : 'Click to select multiple images'}
                </span>
                <p className="text-sm text-white/50 mt-2">
                  Supports JPEG, PNG, and WebP formats
                </p>
              </div>
            </label>
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div className="bg-white/10 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-white/70">Uploading images...</span>
                <span className="text-sm text-white/70">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-2">
                <div 
                  className="bg-[#E85C0D] h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Selected Images */}
          {imageFiles.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-white mb-4">
                Selected Images ({imageFiles.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                {imageFiles.map((imageFile, index) => (
                  <div key={index} className="bg-white/10 rounded-lg p-4">
                    <div className="flex gap-4">
                      <img
                        src={imageFile.preview}
                        alt={`Preview ${index + 1}`}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                      <div className="flex-1 space-y-2">
                        <input
                          type="text"
                          value={imageFile.title}
                          onChange={(e) => updateImageData(index, 'title', e.target.value)}
                          placeholder="Image title"
                          className="w-full px-3 py-1 bg-white/10 border border-white/20 rounded text-white text-sm"
                        />
                        <textarea
                          value={imageFile.description}
                          onChange={(e) => updateImageData(index, 'description', e.target.value)}
                          placeholder="Image description"
                          rows={2}
                          maxLength={100}
                          className="w-full px-3 py-1 bg-white/10 border border-white/20 rounded text-white text-sm resize-none"
                        />
                        <p className="text-xs text-white/50">
                          {imageFile.description.length}/100 characters
                        </p>
                      </div>
                      <button
                        onClick={() => removeImage(index)}
                        className="p-2 text-red-500 hover:bg-white/10 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={handleClose}
              disabled={uploading}
              className="px-6 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleBatchImport}
              disabled={uploading || imageFiles.length === 0}
              className="px-6 py-2 bg-[#E85C0D] text-white rounded-lg hover:bg-[#ff6a0d] transition-colors disabled:opacity-50"
            >
              {uploading ? `Uploading... (${uploadProgress}%)` : `Import ${imageFiles.length} Images`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BatchImportModal;